/* eslint-disable no-undef */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '@/services/api/services/authService';
import { jwtDecode } from 'jwt-decode';
// useSessionTimeout removido - ahora manejamos timeout via JWT + interceptor

// Helper para logs solo en desarrollo
const isDev =
  import.meta.env.MODE === 'development' ||
  import.meta.env.MODE === 'staging' ||
  window.location.hostname === 'localhost';
const devLog = (...args: unknown[]) => {
  if (isDev) {
    console.log(...args);
  }
};

interface User {
  id: string;
  username: string;
  role: string;
  name: string;
  surname: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  login: (token: string, refreshToken: string, user: User) => void;
  logout: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setToken] = useState<string | null>(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      try {
        const decoded = jwtDecode<{ exp: number }>(storedToken);
        if (decoded.exp * 1000 > Date.now()) {
          return storedToken;
        }
      } catch (error) {
        devLog('Error al decodificar el token:', error);
      }
    }
    return null;
  });

  const [refreshToken, setRefreshToken] = useState<string | null>(() => {
    return localStorage.getItem('refreshToken');
  });

  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!token);
  const [error, setError] = useState<string | null>(null);

  const login = (newToken: string, newRefreshToken: string, userData: User) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('refreshToken', newRefreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(newToken);
    setRefreshToken(newRefreshToken);
    setUser(userData);
    setIsAuthenticated(true);
    setError(null);
  };

  const logout = async () => {
    try {
      await authService.logout();
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setToken(null);
      setRefreshToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
    }
  };

  // Session timeout ahora manejado por JWT + interceptor (más simple)

  // Función de refresh token (mantenida por compatibilidad, pero no se usa automáticamente)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const refreshAuthToken = async (): Promise<boolean> => {
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await authService.refreshToken(refreshToken);
      if (response.success && response.data) {
        localStorage.setItem('token', response.data.token);
        if (response.data.refreshToken) {
          localStorage.setItem('refreshToken', response.data.refreshToken);
          setRefreshToken(response.data.refreshToken);
        }
        setToken(response.data.token);
        return true;
      }
    } catch (error) {
      devLog('Error refreshing token:', error);
    }

    // If refresh fails, logout user
    await logout();
    return false;
  };

  // Verificar expiración del token cada 5 minutos (reducido de 1 minuto)
  useEffect(() => {
    const checkTokenExpiration = () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const decoded = jwtDecode<{ exp: number }>(storedToken);
          if (decoded.exp * 1000 < Date.now()) {
            logout();
          }
        } catch (error) {
          devLog('Error al decodificar el token:', error);
          logout();
        }
      }
    };

    // Verificar cada 5 minutos en lugar de cada minuto
    const interval = setInterval(checkTokenExpiration, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, token, login, logout, error }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
